import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import apiAxios from "helpers/apiAxios";
import { getPaymentRequest } from "redux/erp/paymentRequestSlice";
import { Button } from "components/common";

// SmartCA certificate signing for the Head of Finance / BOD step. A request lives
// about 5 minutes in the VNPT SmartCA app; signers rarely confirm that fast after the
// email, so an expired (or unconfirmed) request is sent again from here by the signer.
const POLL_MS = 5000;

const remaining = (expiresAt, now) => {
  const ms = expiresAt ? new Date(expiresAt).getTime() - now : 0;
  if (!(ms > 0)) return null;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const TEXT = {
  not_started: "Not sent yet — send the request to your SmartCA app, confirm it there, then approve.",
  sending: "Sending to SmartCA…",
  pending: "Waiting for you to confirm on the VNPT SmartCA app.",
  expired: "The SmartCA request expired before it was confirmed. Send it again and confirm it in the app.",
  unknown: "SmartCA did not confirm the request. Send it again.",
  declined: "The request was declined on the SmartCA app. Send it again to sign.",
  verified: "Signed and verified — the step is approved once all checks pass.",
};

export default function SmartCaSignCard({ paymentRequest: pr }) {
  const dispatch = useDispatch();
  const signing = pr.certificate_approval || {};
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const status = signing.status || "not_started";
  const left = status === "pending" ? remaining(signing.expires_at, now) : null;
  const shownStatus = status === "pending" && signing.expires_at && !left ? "expired" : status;

  const call = useCallback(async (name, data, quiet = false) => {
    setBusy(true);
    try {
      const res = await apiAxios.post(`/erp/payment_requests/${pr.id}/${name}`, data);
      await dispatch(getPaymentRequest(pr.id)).unwrap();
      const result = res?.data?.data || {};
      // A verified signature approves the step on the server (even on a quiet poll).
      if (result.approved) toast.success("Signed with SmartCA — your step is approved.");
      else if (result.approve_error) toast.error(`Signed with SmartCA, but not approved yet: ${result.approve_error}`, { autoClose: 12000 });
      else if (!quiet) toast.success(name === "certificate_sign" ? "Sent — confirm it on your SmartCA app." : "SmartCA status updated.");
    } catch (error) {
      if (!quiet) toast.error(error?.response?.data?.error || "SmartCA request failed.");
    } finally {
      setBusy(false);
    }
  }, [dispatch, pr.id]);

  // Countdown, and a quiet status check every few seconds while the request is live.
  useEffect(() => {
    if (status !== "pending") return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => call("certificate_status", {}, true), POLL_MS);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [status, call]);

  if (!signing.required) return null;
  const canResend = signing.resendable || shownStatus === "expired";

  return (
    <section aria-label="SmartCA signature" className="tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-bg-geist-bg tw-p-4 tw-text-sm">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <i className="bi bi-shield-lock tw-text-geist-accent" />
        <span className="tw-font-semibold tw-text-geist-fg">Digital signature (VNPT SmartCA)</span>
        <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold ${
          shownStatus === "verified" ? "tw-bg-[#ecfdf3] tw-text-[#067647]" : "tw-bg-[#fff8ee] tw-text-[#a05e03]"}`}>
          {shownStatus === "pending" && left ? `Waiting · ${left} left` : shownStatus.replace("_", " ")}
        </span>
        {signing.attempts > 1 && <span className="tw-text-[11px] tw-text-geist-fg-3">attempt {signing.attempts}</span>}
      </div>
      <p className="tw-m-0 tw-mt-1.5 tw-text-[13px] tw-text-geist-fg-2">{TEXT[shownStatus] || TEXT.unknown}</p>
      {!signing.configured && <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-geist-error">SmartCA connection or your certificate is not set up — ask an admin.</p>}
      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
        {shownStatus === "not_started" && (
          <Button size="sm" disabled={busy || !signing.configured} onClick={() => call("certificate_sign", {})}>Send to my SmartCA app</Button>
        )}
        {canResend && shownStatus !== "verified" && (
          <Button size="sm" disabled={busy || !signing.configured} onClick={() => call("certificate_sign", { resend: true })}>Send again to my SmartCA app</Button>
        )}
        {["pending", "unknown", "expired"].includes(shownStatus) && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => call("certificate_status", {})}>Check now</Button>
        )}
      </div>
    </section>
  );
}

SmartCaSignCard.propTypes = { paymentRequest: PropTypes.object.isRequired };
